import {
  Button,
  InputAdornment,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import CustomTextInput from "../../../../../global/components/CustomTextInput/CustomTextInput";
import { handleErrorMessage, isTruthy } from "helpers/methods";
import { cloneDeep } from "lodash";
import { SocialCampaignContext } from "../Context";
import CustomAutoCompleteNew from "global/components/CustomAutoCompleteNew/CustomAutoCompleteNew";
import {
  getIntegratedAppNames,
  getUserDetailToFB,
} from "screens/Integration/Component/FacebookService";
import strings from "global/constants/StringConstants";
import { RouterLink } from "components/router-link";
import viewpaths from "global/constants/ViewPathConstants";
import history from "utils/history";

interface CustomProps {
  setSkipStepOne: Function;
}

const Step1 = (props: CustomProps) => {
  const {
    submitCampaign,
    setSubmitCampaign,
    socialApps,
    setSocialApps,
    pages,
    setPages,
    pageDetails,
    setPageDetails,
  } = useContext(SocialCampaignContext);
  const [formFields, setFormFields] = useState({
    name: {
      value: submitCampaign.name ?? "",
      error: "",
    },
    selectedPlatform: {
      value: submitCampaign.socialAppName ?? "",
      error: "",
    },
    selectedPage: {
      label: {
        value: submitCampaign.pageName ?? "",
        error: "",
      },
      pageId: {
        value: submitCampaign.socialCampaign.pageId ?? "",
        error: "",
      },
      profilePicture: {
        value: submitCampaign.profilePicture ?? "",
        error: "",
      },
      userName: {
        value: submitCampaign.userName ?? "",
        error: "",
      },
    },
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getIntegratedSocialApps();
  }, []);

  const getIntegratedSocialApps = async () => {
    try {
      setLoading(true);
      const integrationApps = await getIntegratedAppNames();
      if (isTruthy(integrationApps)) {
        const responseUserDetails = await getUserDetailToFB(strings.FACEBOOK);
        setPageDetails(responseUserDetails);
        const hasInstagram = responseUserDetails?.metaData?.pageDetails?.some(
          (page: any) => page.instagram_business_account
        );
        let socialAppsList = integrationApps.map((app: any) => ({
          label: app,
          value: app,
        }));

        if (hasInstagram) {
          socialAppsList.push({
            label: strings.INSTAGRAM,
            value: strings.INSTAGRAM,
          });
        }
        setSocialApps(socialAppsList);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const saveCampaignName = () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    if (!isTruthy(clonedFormFields.name.value)) {
      error = true;
      clonedFormFields.name.error = "Please enter the campaign name";
    }
    if (!isTruthy(clonedFormFields.selectedPlatform.value)) {
      error = true;
      clonedFormFields.selectedPlatform.error = "Please select platform";
    }
    if (!isTruthy(clonedFormFields.selectedPage.label.value)) {
      error = true;
      clonedFormFields.selectedPage.label.error =
        "Please select page to post on";
    }
    if (error) {
      return setFormFields(clonedFormFields);
    }

    setSubmitCampaign({
      ...submitCampaign,
      name: formFields.name.value.trim(),
      socialAppName: formFields.selectedPlatform.value,
      socialSource: formFields.selectedPage.userName.value,
      profilePicture: formFields.selectedPage.profilePicture.value,
      userName: formFields.selectedPage.userName.value,
      pageName: formFields.selectedPage.label.value,
      socialCampaign: {
        ...submitCampaign.socialCampaign,
        pageId: formFields.selectedPage.pageId.value,
      },
    });
    props.setSkipStepOne(true);
  };

  const handlePlatFormChange = (
    event: any,
    newValue: { label: string; value: string }
  ) => {
    const pageList = pageDetails?.metaData?.pageDetails || [];

    const filteredPages = pageList
      .filter((page: any) =>
        newValue.value === strings.INSTAGRAM
          ? page.instagram_business_account
          : true
      )
      .map((page: any) => {
        if (newValue.value === strings.INSTAGRAM) {
          const instagramAccount = page.instagram_business_account;
          return {
            label: instagramAccount.username,
            value: instagramAccount.id,
            profile_picture: instagramAccount.profile_picture_url,
            userName: instagramAccount.username,
          };
        } else {
          return {
            label: page.name,
            value: page.id,
            profile_picture: pageDetails?.metaData.profilePicture ?? "",
            userName: pageDetails?.metaData.userName ?? "",
          };
        }
      });

    setPages(filteredPages);
    setFormFields((prevFormFields) => {
      const isPlatformChanged =
        prevFormFields.selectedPlatform.value !== newValue.value;

      return {
        ...prevFormFields,
        selectedPlatform: {
          error: "",
          value: newValue?.value || "",
        },
        ...(isPlatformChanged && {
          selectedPage: {
            label: {
              error: "",
              value: "",
            },
            userName: {
              error: "",
              value: "",
            },
            pageId: {
              error: "",
              value: "",
            },
            profilePicture: {
              error: "",
              value: "",
            },
          },
        }),
      };
    });
  };

  const Step1Skeleton = () => {
    return (
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant="h4">
            <Skeleton width={250} />
          </Typography>
          <Typography variant="body1">
            <Skeleton width="80%" />
            <Skeleton width="60%" />
          </Typography>
        </Stack>
        <Skeleton variant="rectangular" height={56} width="100%" />
        <Skeleton variant="rectangular" height={56} width="100%" />
        <Skeleton variant="rectangular" height={56} width="100%" />
      </Stack>
    );
  };

  const getPage = () => {
    return (
      <Stack spacing={4}>
        <Stack spacing={1}>
          <Typography variant="h4">Create a social campaign</Typography>
          <Typography variant="body1">
            To get started, please provide a name for your social campaign. This
            will help you easily identify and manage your campaign later on.
          </Typography>
        </Stack>
        <CustomTextInput
          label="Campaign name"
          value={formFields.name.value}
          onChange={(event: any) => {
            setFormFields({
              ...formFields,
              name: {
                value: event.target.value,
                error: "",
              },
            });
          }}
          error={formFields.name.error}
          required
          placeHolder="Enter the campaign name"
          disabled={!isTruthy(socialApps)}
          propsToInputElement={{ minLength: 1, maxLength: 90 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <span style={{ fontSize: "0.75rem" }}>
                  {formFields.name?.value?.length} / 90
                </span>
              </InputAdornment>
            ),
          }}
        />
        <CustomAutoCompleteNew
          options={socialApps}
          renderOption={(props, option) => {
            return <li {...props}>{option.label}</li>;
          }}
          id={"from-section-email-address"}
          onChange={(event: any, newValue: { label: string; value: string }) =>
            handlePlatFormChange(event, newValue)
          }
          value={formFields.selectedPlatform.value}
          label="Platforms"
          placeHolder="Select the platform"
          error={formFields.selectedPlatform.error}
          clearIcon={false}
          disabled={!isTruthy(socialApps)}
        />
        <CustomAutoCompleteNew
          options={pages}
          renderOption={(props, option) => {
            return <li {...props}>{option.label}</li>;
          }}
          id={"from-section-email-address"}
          onChange={(event: any, newValue: any) => {
            setFormFields({
              ...formFields,
              selectedPage: {
                label: {
                  error: "",
                  value: newValue?.label ? newValue?.label : "",
                },
                userName: {
                  error: "",
                  value: newValue?.userName ? newValue?.userName : "",
                },
                pageId: {
                  error: "",
                  value: newValue?.value ? newValue?.value : "",
                },
                profilePicture: {
                  error: "",
                  value: newValue?.profile_picture ?? "",
                },
              },
            });
          }}
          value={formFields.selectedPage.label.value}
          label="Post to"
          placeHolder="Select the page"
          error={formFields.selectedPage.label.error}
          disabled={!isTruthy(socialApps)}
        />
        {isTruthy(socialApps) ? (
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              variant="text"
              // onClick={() => history.push(viewpaths.campaignsViewPath)}
              onClick={() =>
                history.push(
                  `${
                    viewpaths.campaignsViewPath
                  }?page=${1}&rowsPerPage=${10}&status=All&category=all&tab=social`
                )
              }
            >
              Cancel
            </Button>

            {isTruthy(socialApps) && (
              <Button variant="contained" onClick={saveCampaignName}>
                Create campaign
              </Button>
            )}
          </Stack>
        ) : (
          <Stack sx={{ backgroundColor: "#DCE2EB", borderRadius: "8px" }} p={2}>
            <Typography>
              There are no accounts connected. Please{" "}
              <Link
                href={viewpaths.integrationsViewPath}
                component={RouterLink}
              >
                click here
              </Link>{" "}
              to get started connecting your social media accounts.
            </Typography>
          </Stack>
        )}
      </Stack>
    );
  };

  return loading ? Step1Skeleton() : getPage();
};

export default Step1;
